import { jwtDecode } from "jwt-decode";
import { CapacitorHttp, HttpResponse } from "@capacitor/core";
import { CapacitorCookies } from "@capacitor/core";
import { options } from "ionicons/icons";
import { createContext, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { useToast } from "../components/ToastContext";
type AuthPayload = {
  username: string;
  password: string;
};

type LoginResponse = { token: string; refresh_token: string };

type TokenData = {
  apps: Record<string, string>;
  username: string;
  exp: number;
  iat: number;
  roles: string[];
  userIri: string;
};

export interface AuthContextType {
  token: string | null;
  user?: TokenData;
  login: (payload: AuthPayload) => Promise<LoginResponse>;
  signout: (callback: VoidFunction) => void;
  setToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  updateToken: () => void;
}

const baseUrl = import.meta.env.VITE_BASE_URL;

const requestOptions = {
  headers: { "content-type": "application/json" },
};

const setCapacitorCookie = async (key: string, value: string) => {
  await CapacitorCookies.setCookie({
    key: key,
    value: value,
  });
};

const deleteCookie = async (key: string) => {
  await CapacitorCookies.deleteCookie({
    key: key,
  });
};

export const getCookies = async () => {
  return await CapacitorCookies.getCookies();
};

const auth = {
  async login(payload: AuthPayload) {
    const response = await CapacitorHttp.post({
      url: `${baseUrl}/driver/login`,
      data: { gasoilCode: payload.password },
      ...requestOptions,
    });
    if (response.data.token) {
      await setCapacitorCookie("token", response.data.token);
      await setCapacitorCookie("refresh_token", response.data.refresh_token);
    }
    return response.data;
  },
  async refreshToken(refreshToken: string) {
    const response = await CapacitorHttp.post({
      url: `${baseUrl}/driver/auth/refresh`,
      data: { refresh_token: refreshToken },
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
      },
    });
    if (response.data.token && response.data.refresh_token) {
      await setCapacitorCookie("token", response.data.token);
      await setCapacitorCookie("refresh_token", response.data.refresh_token);
    }
    return response.data;
  },
  async signout() {
    await deleteCookie("token");
    await deleteCookie("refresh_token");
  },
};

export const AuthContext = createContext<AuthContextType>(null!);

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  let [token, setToken] = useState<null | string>(null);
  let [refreshToken, setRefreshToken] = useState<null | string>(null);

  useEffect(() => {
    getCookies().then((response) => {
      setToken(response.token);
      setRefreshToken(response.refresh_token);
    });
  }, []);

  let [user, setUser] = useState<TokenData>();

  let login = async (newUser: AuthPayload) => {
    const { token, refresh_token } = await auth.login(newUser);

    setToken(token);

    return { token, refresh_token };
  };

  let signout = () => {
    auth.signout();
    setUser(undefined);
    setToken(null);
  };
  let updateToken = async () => {
    const res = await auth.refreshToken(refreshToken as string);
    setToken(res.token);
    setRefreshToken(res.refresh_token);
  };

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode<TokenData>(token);

      setUser(decoded);
    }
  }, [token]);

  const value = {
    token,
    user,
    login,
    signout,
    updateToken,
    setToken,
    setRefreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = useAuth();

  const history = useHistory();

  useEffect(() => {
    if (auth.token === null) return;
    if (!auth.token) {
      history.replace("/login");
    }
  }, [auth.token, history]);

  if (!auth.token) {
    return null;
  }

  return children;
}

export const CapacitorHttpAuth = async (
  options: any
): Promise<HttpResponse> => {
  let cookies = await getCookies();

  if (cookies.token) {
    options.headers = {
      ...options.headers,
      "content-type":
        options.headers?.["content-type"] || "application/ld+json",
      Authorization: `Bearer ${cookies.token}`,
    };
  }

  try {
    const response = await CapacitorHttp.request(options);
    if (response.status === 401) {
      try {
        let token = await auth.refreshToken(cookies.refresh_token);
        options.headers.Authorization = `Bearer ${token.token}`;
        return await CapacitorHttp.request(options);
      } catch (refreshError) {
        console.error("Failed to refresh token", refreshError);
      }
    }

    return response;
  } catch (error: any) {
    throw error;
  }
};
